import React, { useEffect, useState } from 'react';
import { fetchLiveTasks } from '../api/liveTasks';
import { fetchWorkers } from '../api/workers';
import { RiCustomerService2Fill } from "react-icons/ri";
import { TbPhoneCalling } from "react-icons/tb";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { MdOutlinePhoneMissed } from "react-icons/md";
// import Lottie from 'lottie-react';

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

const statusLabels = {
  pending: 'In wacht',
  assigned: 'In oproep',
};

const styles = {
  groupContainer: {
    marginTop: '1rem',
    borderRadius: '0.5rem',
    padding: '1rem',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: '600',
    marginBottom: '0.75rem',
    color: '#057fa2',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingBottom: '0.25rem',
  },
  countBadge: {
    padding: '0.85rem 1.25rem',
    borderRadius: 10,
    background: '#fff',
    boxShadow: '0 1px 4px rgba(14,66,106,0.04)',
    fontFamily: 'inherit',
    fontSize: '1.12rem',
    border: '1.5px solid #dbe6ef',
    marginBottom: 18,
  },
  taskWrapper: {
    display: 'flex',
    width: '100%',
    gap: '0.5rem',
    alignItems: 'stretch', // <-- change from 'center' to 'stretch' to make children fill height
    // optionally: minHeight or fixed height to ensure .taskWrapper itself has height
    position: 'relative',
  },
  taskRow: {
    borderBottom: '1px solid #f3f4f6',
    backgroundColor: '#f9fafb',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    marginTop: '0.5rem',
    display: 'flex',
    flex: '1 1 100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.5rem',
    color: '#374151',
  },
  taskRowFullWidth: {
    flexGrow: 1,
  },
  customer: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#057fa2',
    width: '50%',
  },
  timer: {
    fontSize: '2rem',
    color: '#4b5563',
    width: '20%',
    textAlign: 'right',
    fontWeight: '500',
  },
  worker: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    fontSize: '1.5rem',
    color: '#4b5563',
    fontWeight: '500',
    width: '30%',
  },
  badgeContainer: {
    display: 'flex',
    flex: '1 1',
    padding: '0.3rem',
    flexDirection: 'row',
    gap: '0.5rem',
    height: '100%',
    justifyContent: 'center',
  },

  rejectedBadge: {
    background: '#fee2e2',
    marginTop: '0.5rem',
    color: '#b91c1c',
    borderRadius: '0.5rem',
    padding: '0.3rem 0.6rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    height: '3rem',   
    width: '100%',        // make badge fill the container vertically
  },
  missedBadge: {
    background: '#fef3c7',
    color: '#92400e',
    borderRadius: '0.5rem',
    padding: '0.3rem 0.6rem',
    marginTop: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    height: '3rem',   
    width: '100%',
  },
};

export default function LiveVoiceTasks({ env = 'memo', visibleTeams: propVisibleTeams, maximized = false, selectedTaskQueues = [] }) {
  const [tasks, setTasks] = useState([]);
  const [now, setNow] = useState(new Date());
  const [workers, setWorkers] = useState([]);
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchLiveTasks(env);
        setTasks(data);
      } catch (err) {
        console.error('Failed to fetch live tasks:', err);
      }
    };
    load();
    const refreshInterval = setInterval(load, 8000);
    const timerInterval = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(timerInterval);
    };
  }, [env]);

  useEffect(() => {
    const loadWorkers = async () => {
      try {
        const data = await fetchWorkers(env);
        const parsed = data.map(w => ({
          ...w,
          attributes: typeof w.attributes === 'string' ? JSON.parse(w.attributes) : w.attributes,
        }));
        setWorkers(parsed);
      } catch (err) {
        console.error('Failed to fetch workers:', err);
      }
    };
    loadWorkers();
    const interval = setInterval(loadWorkers, 20000);
    return () => clearInterval(interval);
  }, [env]);

  function formatWorkerName(email) {
    if (!email) return '';
    const namePart = email.split('@')[0];
    return namePart
      .split('.')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  function getRelevantWorker(task) {
    const { reservations = [], workerName: original } = task;
    if (reservations.length === 0) return original || null;
    if (reservations.length === 1) return original || reservations[0].workerName;

    // multiple reservations: choose latest with relevant status
    const targetStatus = task.assignmentStatus === 'pending' ? 'reserved'
                        : task.assignmentStatus === 'assigned' ? 'accepted'
                        : null;
    if (!targetStatus) return original || null;

    const filtered = reservations
      .filter(r => r.reservationStatus === targetStatus)
      .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
    return filtered.length > 0 ? filtered[0].workerName : original || null;
  }

  function getTeamForWorker(workerName) {
    if (!workerName) return null;
    const w = workers.find(w =>
      w.friendlyName === workerName ||
      w.workerName === workerName ||
      w.attributes?.email === workerName
    );
    const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
    const name = (typeof raw === 'string' ? raw : '').trim();
    return name || null;
  }

  function isTaskVisible(task) {
    const team = getTeamForWorker(task.workerName);
    // Prefer SID-based filtering; fallback to friendlyName if SID is absent
    const queueMatch = !selectedTaskQueues.length
      || selectedTaskQueues.includes(task.taskQueueSid)
      || selectedTaskQueues.includes(task.taskQueueFriendlyName);
    // If no team assigned, don't hide the task due to team filter
    if (!team) return queueMatch;
    const teamFilterPass = propVisibleTeams && propVisibleTeams.size > 0 ? propVisibleTeams.has(team) : true;
    return teamFilterPass && queueMatch;
  }

  const groupedTasks = { pending: [], assigned: [], completed: [] };
  tasks.forEach(task => {
    if ((task.assignmentStatus === 'pending' || task.assignmentStatus === 'reserved') && task.direction === 'outbound') return;

    const resolvedWorkerName = getRelevantWorker(task);
    const base = { ...task, isRinging: task.assignmentStatus === 'reserved', workerName: resolvedWorkerName };

    if (task.assignmentStatus === 'reserved') {
      groupedTasks.pending.push(base);
    } else if (groupedTasks[task.assignmentStatus]) {
      groupedTasks[task.assignmentStatus].push(base);
    }
  });

  const pendingInbound = groupedTasks.pending.filter(
    t => t.direction !== 'outbound' && isTaskVisible(t)
  );

  const longestPending = pendingInbound.length
    ? pendingInbound.reduce((min, t) => new Date(t.dateCreated) < new Date(min.dateCreated) ? t : min, pendingInbound[0])
    : null;
  const totalPending = pendingInbound.length;

  return (
    <div style={{ position: 'relative' }}>
      {/* Top row */}
      <div style={{ display: 'flex', gap: '1rem', flexDirection: isMobile ? 'column' : 'row', marginBottom: longestPending ? 0 : '2rem' }}>
        <div style={{ flex: 1, background: 'rgba(163, 214, 52, 0.08)', borderRadius: '0.5rem', padding: isMobile ? '1rem 1.25rem' : '3rem 5rem', maxHeight: isMobile ? 'auto' : '200px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: isMobile ? '1.5rem' : '3.5rem', color: 'rgb(5, 127, 162)' }}>Totaal in wacht</div>
          <div style={{ fontSize: isMobile ? '2.5rem' : '5rem', fontWeight: 700, color: '#057fa2' }}>{totalPending}</div>
        </div>

        {longestPending && (
          <div style={{ flex: 1, background: 'rgba(239, 68, 68, 0.08)', borderRadius: '0.5rem', padding: isMobile ? '0.75rem 1rem' : '1rem 2rem', maxHeight: isMobile ? 'auto' : '200px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: isMobile ? '0.75rem' : '2rem', flexDirection: isMobile ? 'column' : 'row' }}>
              <span style={{ fontSize: isMobile ? '1.4rem' : '3.5rem', fontWeight: 700, color: '#057fa2', width: isMobile ? '100%' : '80%', textAlign: isMobile ? 'left' : 'inherit' }}>
                {longestPending.attributes?.customers.customer_attribute_2?.split('=')[0].trim() || 'Onbekende beller'}
              </span>
              <span style={{ fontSize: isMobile ? '2.2rem' : '5rem', color: '#057fa2', width: isMobile ? '100%' : '20%', textAlign: 'right', paddingRight: isMobile ? '0' : '2rem', fontWeight: '700' }}>
                {formatDuration(Math.floor((now - new Date(longestPending.dateCreated)) / 1000))}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Task groups */}
      {Object.entries(groupedTasks).map(([status, group]) => {
        if (status === 'completed' || group.length === 0) return null;
        const visible = group.filter(isTaskVisible);
        if (!visible.length) return null;

        return (
          <div key={status} style={styles.groupContainer}>
            <div style={{ ...styles.sectionTitle, fontSize: isMobile ? '1.25rem' : styles.sectionTitle.fontSize }}>
              <span>{statusLabels[status]}</span>
              <span style={{ ...(status === 'pending' ? { ...styles.countBadge, backgroundColor: 'rgba(239, 68, 68, 0.25)' } : styles.countBadge), fontSize: isMobile ? '1.25rem' : styles.countBadge.fontSize, padding: isMobile ? '0.25rem 1rem' : styles.countBadge.padding }}>
                <TbPhoneCalling />
                {visible.length}
              </span>
            </div>

            {visible.map(task => {
              const rejected = (task.reservations || []).filter(r => r.reservationStatus === 'rejected').length;
              const missed = (task.reservations || []).filter(r => r.reservationStatus === 'timeout').length;
              const created = new Date(task.dateCreated);
              const updated = new Date(task.dateUpdated);
              const secsQueue = Math.floor((now - created) / 1000);
              const secsCall = Math.floor((now - updated) / 1000);

              return (
                <div style={styles.taskWrapper} key={task.sid}>
                  <div style={{
                    ...styles.taskRow,
                    fontSize: isMobile ? '0.9rem' : styles.taskRow.fontSize,
                    ...(rejected === 0 && missed === 0 ? styles.taskRowFullWidth : { width: isMobile ? '100%' : 'calc(100% - 130px)' }),
                    backgroundColor: status === 'pending' && task.isRinging ? 'rgba(252, 211, 77, 0.25)' : styles.taskRow.backgroundColor,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? '0.25rem' : styles.taskRow.gap,
                    paddingRight: isMobile && status === 'pending' && (rejected > 0 || missed > 0) ? '3.5rem' : undefined,
                  }}>
                    <span style={{ ...styles.customer, width: isMobile ? '100%' : styles.customer.width }}>
                      {(status === 'pending' || status === 'assigned') && (
                        <>
                          <span style={{ fontWeight: 700, color: '#ef4444', marginRight: '0.8rem', fontSize: isMobile ? '1rem' : '1.3rem' }}>
                            {task.priority != null ? `P${task.priority}` : ''}
                          </span>
                          {status === 'assigned' && task.direction && (
                            <span style={{ fontWeight: 700, color: '#057fa2', marginRight: '0.8rem', fontSize: isMobile ? '0.9rem' : '1.1rem', textTransform: 'capitalize' }}>
                              {task.direction}
                            </span>
                          )}
                          {status === 'pending' && task.isRinging && (
                            <span style={{ fontWeight: 700, color: '#f59e42', marginRight: '0.8rem', fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
                              Ringing
                            </span>
                          )}
                        </>
                      )}
                      <span style={{ fontSize: isMobile ? '1rem' : 'inherit' }}>
                        {task.attributes?.customers.customer_attribute_2?.split('=')[0].trim() || 'Onbekende beller'}
                      </span>
                      <span style={{ marginLeft: isMobile ? '0.5rem' : '1rem', color: 'orange', fontSize: isMobile ? '0.85rem' : 'inherit' }}>{task.taskQueueFriendlyName}</span>
                    </span>
                    {(status === 'pending' ? task.isRinging : status === 'assigned') && task.workerName && (
                      <span style={{ ...styles.worker, fontSize: isMobile ? '1rem' : styles.worker.fontSize, width: isMobile ? '100%' : styles.worker.width }}>
                        <RiCustomerService2Fill />
                        {formatWorkerName(task.workerName)}
                        {getTeamForWorker(task.workerName) && (
                          <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: '0.5rem', padding: '0.15rem 0.5rem', fontSize: isMobile ? '0.75rem' : '1rem', fontWeight: 600, marginLeft: '0.5rem' }}>
                            {getTeamForWorker(task.workerName)}
                          </span>
                        )}
                      </span>
                    )}
                    <span style={{ ...styles.timer, fontSize: isMobile ? '1.25rem' : styles.timer.fontSize, width: isMobile ? '100%' : styles.timer.width, textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? '0.25rem' : 0 }}>
                      {status === 'pending' ? formatDuration(secsQueue) : formatDuration(secsCall)}
                    </span>
                  </div>

                  {status === 'pending' && (rejected > 0 || missed > 0) && (
                    <div
                      style={
                        isMobile
                          ? {
                              position: 'absolute',
                              top: '0.4rem',
                              right: '0.5rem',
                              display: 'flex',
                              gap: '0.4rem',
                              padding: 0,
                              height: 'auto',
                              justifyContent: 'flex-end',
                              zIndex: 2,
                            }
                          : styles.badgeContainer
                      }
                    >
                      {rejected > 0 && (
                        <span
                          style={{
                            ...styles.rejectedBadge,
                            height: isMobile ? '1.8rem' : styles.rejectedBadge.height,
                            fontSize: isMobile ? '0.75rem' : 'inherit',
                            padding: isMobile ? '0.1rem 0.45rem' : styles.rejectedBadge.padding,
                          }}
                        >
                          <HiOutlinePhoneMissedCall size={20} />{rejected}
                        </span>
                      )}
                      {missed > 0 && (
                        <span
                          style={{
                            ...styles.missedBadge,
                            height: isMobile ? '1.8rem' : styles.missedBadge.height,
                            fontSize: isMobile ? '0.75rem' : styles.missedBadge.fontSize,
                            padding: isMobile ? '0.1rem 0.45rem' : styles.missedBadge.padding,
                          }}
                        >
                          <MdOutlinePhoneMissed size={20} />{missed}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {totalPending >= 15 && (
        <div style={{
          position: 'fixed', left: 0, opacity: '0.5', bottom: '-30rem',
          fontSize: '10rem', pointerEvents: 'none', userSelect: 'none',
          color: '#fca311', fontWeight: '800', transform: 'rotate(-60deg)',
        }}>15+</div>
      )}
    </div>
  );
}